import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBriefcase, FaChartLine, FaGlobe, FaMoneyBillWave, FaUser, FaUserShield, FaWallet } from 'react-icons/fa';

import { AuthContext } from '../../context/AuthContext';
import { fetchUserProfile } from '../../api/profile';
import { Button } from '../common/Button';

const UserIcon = FaUser;
const ShieldIcon = FaUserShield;
const MoneyIcon = FaMoneyBillWave;
const ChartIcon = FaChartLine;
const WalletIcon = FaWallet;
const GlobeIcon = FaGlobe;
const BriefcaseIcon = FaBriefcase;

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '*';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '*';
    }
  }
  return String(value);
};

const InfoCard = ({ icon, label, value }) => (
  <div className="group bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:bg-gray-50 transition-all duration-300 hover:shadow-md">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
        <div className="mt-2 text-base font-semibold text-gray-900 break-words">{formatValue(value)}</div>
      </div>
      {icon ? <div className="text-gray-700 mt-0.5 shrink-0">{icon}</div> : null}
    </div>
  </div>
);

const SummaryCard = ({ title, value, icon }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 hover:-translate-y-1 hover:shadow-md hover:border-gray-300 transition-all duration-300">
    <div className="flex items-center justify-between gap-3">
      <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
      {icon ? <div className="text-gray-700">{icon}</div> : null}
    </div>
    <div className="mt-3 text-lg font-semibold text-gray-900 break-words">{formatValue(value)}</div>
  </div>
);

const FieldCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:bg-gray-50 transition-all duration-300 hover:shadow-md">
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-2 text-base font-semibold text-gray-900 break-words">{formatValue(value)}</div>
  </div>
);

const SectionCard = ({ icon, title, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-5">
    <div className="flex items-center gap-3 mb-5">
      <div className="text-gray-700">{icon}</div>
      <div className="text-xl font-semibold text-gray-900">{title}</div>
    </div>
    <div className="border-t border-gray-100 mb-5" />
    {children}
  </div>
);

const ProfileHeader = ({ userObj, navigate }) => {
  const first = userObj?.first_name;
  const last = userObj?.last_name;

  const initials = (() => {
    const f = typeof first === 'string' ? first.trim() : '';
    const l = typeof last === 'string' ? last.trim() : '';
    const a = f ? f[0].toUpperCase() : '';
    const b = l ? l[0].toUpperCase() : '';
    const s = `${a}${b}`.trim();
    return s || 'U';
  })();

  const fullName = (() => {
    const f = first ? String(first).trim() : '';
    const l = last ? String(last).trim() : '';
    const name = `${f} ${l}`.trim();
    return name || 'Unknown';
  })();

  const Badge = ({ children }) => (
    <span className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-900 shadow-sm">
      {children}
    </span>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-5 min-w-0">
          <div className="w-20 h-20 rounded-full bg-black text-white flex items-center justify-center font-bold text-xl">
            {initials}
          </div>

          <div className="min-w-0">
            <div className="text-xl font-bold text-gray-900 break-words">{fullName}</div>
            <div className="mt-1 text-sm text-gray-700 break-words">{userObj?.email ? String(userObj.email) : '*'}</div>
            <div className="mt-1 text-sm text-gray-600 break-words">{userObj?.username ? String(userObj.username) : '*'}</div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Badge>Verified</Badge>
              <Badge>{formatValue(userObj?.account_nature)}</Badge>
              <Badge>{formatValue(userObj?.role)}</Badge>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            width="w-40"
            className="bg-transparent border-primary text-primary hover:bg-primary hover:text-white"
            onClick={() => navigate('/dashboard/edit-profile')}
          >
            Edit Profile
          </Button>
          <Button
            width="w-40"
            className="bg-transparent border-primary text-primary hover:bg-primary hover:text-white"
            onClick={() => navigate('/dashboard/settings')}
          >
            Change Password
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          title="Referral Code"
          value={userObj?.referral_code}
          icon={<WalletIcon className="text-gray-700" />}
        />
        <SummaryCard
          title="Trading Platform"
          value={userObj?.platforms}
          icon={<ChartIcon className="text-gray-700" />}
        />
        <SummaryCard
          title="Base Currency"
          value={userObj?.base_currency}
          icon={<GlobeIcon className="text-gray-700" />}
        />
        <SummaryCard
          title="Leverage"
          value={userObj?.leverage}
          icon={<BriefcaseIcon className="text-gray-700" />}
        />
      </div>
    </div>
  );
};

const SkeletonBlock = ({ className }) => <div className={`bg-gray-200 animate-pulse rounded ${className}`} />;

const ProfileSkeleton = () => (
  <div className="w-full">
    <SkeletonBlock className="h-6 w-32 mb-4" />
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-5 min-w-0">
          <SkeletonBlock className="w-20 h-20" />
          <div className="min-w-0 w-full">
            <SkeletonBlock className="h-5 w-64 mb-3" />
            <SkeletonBlock className="h-4 w-80 mb-2" />
            <SkeletonBlock className="h-4 w-56 mb-4" />
            <div className="flex flex-wrap gap-2">
              <SkeletonBlock className="h-8 w-28" />
              <SkeletonBlock className="h-8 w-32" />
              <SkeletonBlock className="h-8 w-24" />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <SkeletonBlock className="h-10 w-40" />
          <SkeletonBlock className="h-10 w-40" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
            <SkeletonBlock className="h-3 w-28 mb-4" />
            <SkeletonBlock className="h-5 w-40" />
          </div>
        ))}
      </div>
    </div>

    {Array.from({ length: 4 }).map((_, idx) => (
      <div key={idx} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-5">
        <div className="flex items-center gap-3 mb-5">
          <SkeletonBlock className="w-8 h-8 rounded-lg" />
          <SkeletonBlock className="h-6 w-56" />
        </div>
        <SkeletonBlock className="h-1 w-full mb-5" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((__, j) => (
            <SkeletonBlock key={j} className="h-28 w-full" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Profile = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const userId = user?.user_id;
        if (!userId) return;
        const res = await fetchUserProfile(userId);
        const data = res?.user ?? res;
        setProfileData(data);
      } catch (e) {
        console.error('Failed to fetch profile:', e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  const userObj = useMemo(() => profileData, [profileData]);

  if (loading) {
    return <div className="w-full p-0"><ProfileSkeleton /></div>;
  }

  if (!userObj) {
    return (
      <div className="w-full flex items-center justify-center">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 max-w-md w-full text-center">
          <div className="flex items-center justify-center mb-4 text-gray-700">
            <UserIcon className="text-4xl" />
          </div>
          <div className="text-xl font-semibold text-gray-900">No Profile Found</div>
          <div className="mt-2 text-sm text-gray-600">We couldn’t find your profile data.</div>
          <div className="mt-5 flex justify-center">
            <button
              type="button"
              className="w-full md:w-48 font-bold py-2 px-4 rounded-lg border border-primary text-primary hover:bg-primary hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Profile</h2>
      </div>

      <ProfileHeader userObj={userObj} navigate={navigate} />

      <SectionCard icon={<UserIcon />} title="Account Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldCard label="User ID" value={userObj.id ?? userObj.user_id} />
          <FieldCard label="Email" value={userObj.email} />
          <FieldCard label="Username" value={userObj.username} />
          <FieldCard label="Phone Number" value={userObj.phoneNumber} />
          <FieldCard label="Role" value={userObj.role} />
          <FieldCard label="Referral Code" value={userObj.referral_code} />
          <FieldCard label="Account Nature" value={userObj.account_nature} />
          <FieldCard label="Created At" value={userObj.user_created_at} />
          <FieldCard label="Updated At" value={userObj.user_updated_at} />
        </div>
      </SectionCard>

      <SectionCard icon={<ShieldIcon />} title="Personal Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldCard label="First Name" value={userObj.first_name} />
          <FieldCard label="Last Name" value={userObj.last_name} />
          <FieldCard label="Gender" value={userObj.gender} />
          <FieldCard label="Date of Birth" value={userObj.dob} />
          <FieldCard label="Nationality" value={userObj.Nationality} />
          <FieldCard label="Street" value={userObj.street} />
          <FieldCard label="Address" value={userObj.Address} />
          <FieldCard label="State" value={userObj.State} />
          <FieldCard label="Country" value={userObj.Country} />
        </div>
      </SectionCard>

      <SectionCard icon={<MoneyIcon />} title="Financial Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldCard label="TIN" value={userObj.TIN} />
          <FieldCard label="Industry" value={userObj.industry} />
          <FieldCard label="Employment Status" value={userObj.employment_status} />
          <FieldCard label="Annual Income" value={userObj.annual_income} />
          <FieldCard label="Value of Savings" value={userObj.value_of_savings} />
          <FieldCard label="Total Net Assets" value={userObj.total_net_assets} />
          <FieldCard label="Source of Wealth" value={userObj.source_of_wealth} />
          <FieldCard label="Expected Initial Deposit" value={userObj.expected_initial_amount_of_depsoit} />
        </div>
      </SectionCard>

      <SectionCard icon={<ChartIcon />} title="Trading Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldCard label="Account Type" value={userObj.account_type} />
          <FieldCard label="Trading Experience" value={userObj.trading_experience} />
          <FieldCard label="Platforms" value={userObj.platforms} />
          <FieldCard label="Base Currency" value={userObj.base_currency} />
          <FieldCard label="Leverage" value={userObj.leverage} />
        </div>
      </SectionCard>
    </div>
  );
};

export default Profile;
